import { createRef, useMemo, useState } from "react";
import { Source, Layer } from "react-map-gl";

import {
  BaseMap,
  BaseMapProps,
  MapTooltipProps,
  MapTooltip,
  LightTravelLegTeaser,
} from "shared/components";
import { EventTravelLegType, IDepartureArrival } from "Bands/types";
import { GlobalMapStyle } from "../Map/styles";
import {
  mapMarkersToLines,
  mapMarkersToGeoJson,
  useGetBounds,
  useLoadImageRef,
  useTravelMarkers,
} from "../../utils";
import { lineLayer, markerLayer } from "./layers";
import { Message } from "shared/components";
import { copy } from "shared/assets";
import { TravelLegsMapOverlay } from "./styles";
interface Props extends Omit<BaseMapProps, "coordinates"> {
  travelLegs?: EventTravelLegType[];
}

export const TravelMap: React.FC<Props> = ({ travelLegs, ...rest }) => {
  const mapRef = createRef<any>();

  const { markers } = useTravelMarkers(travelLegs);
  const { coordinates, zoom } = useGetBounds(markers);

  // load icon image
  useLoadImageRef(mapRef);

  // markers
  const points: GeoJSON.FeatureCollection<GeoJSON.Geometry> = useMemo(
    () => mapMarkersToGeoJson(markers),
    [markers]
  );

  // lines between markers
  const lines: GeoJSON.FeatureCollection<GeoJSON.LineString> = useMemo(
    () => mapMarkersToLines(markers),
    [markers]
  );

  const [popup, setPopup] =
    useState<(MapTooltipProps & { id: string }) | null>(null);

  // close popup on click outside lines/markers
  const onClick = (event: any) => {
    const map = mapRef?.current?.getMap();
    const line = event.features?.[0];

    if (!map) return;
    if (line?.source === "lines") return;

    setPopup(null);
  };

  // show popup with info on hover on line/marker
  const onHover = (event: any) => {
    const map = mapRef?.current?.getMap();
    const line = event.features?.[0];

    if (!map) return;
    if (line?.source !== "lines") return;

    if (popup?.id === line.properties.id) return;

    setPopup(null);
    setPopup({
      id: line.properties.id,
      content: (
        <LightTravelLegTeaser
          departure={JSON.parse(line.properties.departure) as IDepartureArrival}
          arrival={JSON.parse(line.properties.arrival) as IDepartureArrival}
        />
      ),
      coordinates: {
        latitude: event.lngLat[1],
        longitude: event.lngLat[0],
      },
    });
  };

  return (
    <>
      <GlobalMapStyle />

      <BaseMap
        mapRef={mapRef}
        {...rest}
        onClick={onClick}
        onHover={onHover}
        coordinates={coordinates}
        zoom={zoom}
        interactiveLayerIds={["lines"]}
      >
        {popup && <MapTooltip {...popup} />}

        {!travelLegs?.length && (
          <TravelLegsMapOverlay>
            <Message
              message={copy.travelBlock.passenger.messages.noTravelLegs}
            />
          </TravelLegsMapOverlay>
        )}

        <Source id="lines" type="geojson" data={lines}>
          <Layer {...lineLayer} />
        </Source>
        <Source id="markers" type="geojson" data={points}>
          <Layer {...markerLayer} />
        </Source>
      </BaseMap>
    </>
  );
};
